import { useIsFocused } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import userRequired from 'hoc/userRequired';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { ActivityIndicator } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { getSpallaCheckSubtitles, getSpallaVideoTextTracksUrl } from 'services/spallaApi';
import { isOffAirTime } from 'utils/date';
import { ProgramType } from 'utils/enum';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { useAutoNext } from '../AutoNext';
import { SpallaLanguageCode, spallaLanguages } from '../SpallaPlayerCTV/controls';
import { VideoInfoView } from '../Video/UI/Info';
import { usePlayer } from '../context';

import { useProgram } from 'components/Program/context';

const bandThumb = require('../../../assets/band_thumb.png') as string;

declare global {
  interface Window {
    SpallaPlayer: any;
  }
}

interface PlayerStartEvent {
  id: string;
  duration?: number;
}

export type SpallaPlayerWebProps = {
  streamId?: string;
  videoId?: string;
  videoIdDub?: string;
  autoplay?: boolean;
  muted?: boolean;
  isLive?: boolean;
  callback?: () => void;
  playerDuration?: number;
  startTime?: number;
  onPlayerEvent?: (e: any) => void;
};

const INIT_TIMEOUT = 2000;

export type SpallaPlayerInstance = {
  on: (event: string, callback: (e: any) => void) => void;
  postMessage?: (message: any) => void;
  play?: () => void;
  pause?: () => void;
  mute?: () => void;
  unmute?: () => void;
  hideSubtitles?: () => void;
  setActiveSubtitle?: (language: string) => void;
  setVolume?: (volume: number) => void;
  playbackRate?: (rate: number) => void;
  currentTime?: (seconds: number) => void;
  forward?: (seconds: number) => void;
  rewind?: (seconds: number) => void;
  bookmarks?: (
    bookmarks: Array<{
      start: number;
      end?: number;
      title?: string;
      color?: string;
    }>
  ) => void;
};

const SpallaPlayerWeb: React.FC<SpallaPlayerWebProps> = ({
  streamId,
  videoId,
  videoIdDub,
  muted = false,
  callback,
  isLive = false,
  playerDuration = 0,
  startTime = 0,
  autoplay = false,
  onPlayerEvent,
}) => {
  const program = useProgram();
  const spallaPlayerRef = useRef<SpallaPlayerInstance | null>(null);
  const { TransmissionScreen, ads } = useGlobalConfig();
  const [isHovered, setIsHovered] = React.useState(false);
  const analytics = useAnalytics();
  const isOffAir = TransmissionScreen?.livestream_offline_mode === true && isLive && isOffAirTime();
  const {
    ref: playerRef,
    isFullScreen,
    isVisible,
    play,
    pause,
    setIsSpallaReady,
    isSpallaReady,
    setIsLiveRealTime,
    spallaConfigRef,
    setupTextTracks,
    setFullScreen,
  } = usePlayer();
  const { selectedProfile } = useSelectedProfile();
  const timeoutRef = useRef<any>(null);
  const isUnder18 = selectedProfile?.ageRating !== '18';
  const [currentTimeState, setCurrentTimeState] = useState(0);
  const isSpallaReadyRef = useRef(false);
  const wrapperRef = useRef(null);
  const isFocused = useIsFocused();

  const { showNextTrack, autoNextCard } = useAutoNext({
    currentTime: isSpallaReadyRef.current ? currentTimeState : 0,
    duration: playerDuration,
    forceMobileView: !isFullScreen,
  });

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    spallaConfigRef.current = {
      ...spallaConfigRef.current,
      videoId,
      videoIdDub,
      currentVideoId: videoIdDub ?? videoId ?? undefined,
      progress: startTime,
    };
  }, [videoId, videoIdDub, startTime]);

  const getSpallaPlayer = () => {
    return spallaPlayerRef.current;
  };

  const playerControls = {
    play: () => {
      const player = getSpallaPlayer();
      if (player?.play) {
        player.play();
        play();
      }
    },
    pause: () => {
      const player = getSpallaPlayer();
      if (player?.pause) {
        player.pause();
        pause();
      }
    },
    mute: () => getSpallaPlayer()?.mute?.(),
    unmute: () => getSpallaPlayer()?.unmute?.(),
    seekTo: (seconds: number) => {
      const player = getSpallaPlayer();
      if (player?.currentTime) {
        player.currentTime(Number(seconds));
      }
    },
    hideSubtitles: () => getSpallaPlayer()?.hideSubtitles?.(),
  };

  const handleStart = useCallback(
    (event: PlayerStartEvent) => {
      const config = spallaConfigRef.current;

      if (isLive || !config.currentVideoId) return;

      playerControls.hideSubtitles();
      config.duration = event.duration ?? 0;

      const currentStart = config.progress || 0;

      const THRESHOLD_PERCENT = 0.96;
      const END_BUFFER_SECONDS = 30;

      const isNearEnd = playerDuration - currentStart <= END_BUFFER_SECONDS;
      const isFinished = playerDuration > 0 && currentStart >= playerDuration * THRESHOLD_PERCENT;

      const finalStartTime = isFinished || isNearEnd ? 0 : currentStart;

      config.progress = finalStartTime;
      setCurrentTimeState(finalStartTime);

      if (finalStartTime > 0) {
        setTimeout(() => {
          playerControls.seekTo(finalStartTime);
        }, 500);
      }
    },
    [isLive, playerDuration]
  );

  useEffect(() => {
    if (!playerRef?.current || isLive) return;

    const setupIframeListener = (iframe) => {
      iframe.addEventListener('load', () => {
        timeoutRef.current = setTimeout(() => {
          if (!isSpallaReadyRef.current) {
            callback?.();
          }
        }, INIT_TIMEOUT);
      });
    };

    const existingIframe = (playerRef.current as any).querySelector('iframe');
    if (existingIframe) {
      setupIframeListener(existingIframe);
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.tagName === 'IFRAME') {
            setupIframeListener(node);
            observer.disconnect();
          }
        }
      }
    });

    observer.observe(playerRef?.current as any, { childList: true });

    return () => observer.disconnect();
  }, [playerRef, isLive, callback]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!spallaConfigRef.current.currentVideoId || program.type === ProgramType.MOVIE) {
        return;
      }

      const apiResponse = await getSpallaVideoTextTracksUrl(spallaConfigRef.current.currentVideoId);

      const textTracks = Object.entries(apiResponse).map(([language, url], index) => ({
        index,
        uri: url,
        language,
        title: spallaLanguages[language as SpallaLanguageCode] || language,
        type: 'application/x-subrip',
      }));

      setupTextTracks(textTracks);
    };

    bootstrap();
  }, [program]);

  const handleSpallaMessage = (event: MessageEvent) => {
    if (!isSpallaReadyRef.current && event.origin.includes('https://beyond.spalla.io')) {
      clearTimeout(timeoutRef.current);
      setIsSpallaReady(true);
      isSpallaReadyRef.current = true;
    }
  };

  const handleFullScreen = () => {
    setFullScreen(!!document.fullscreenElement);
  };

  const destroyPlayer = () => {
    if (spallaPlayerRef.current) {
      try {
        spallaPlayerRef.current.pause?.();
        (spallaPlayerRef.current as any)?.destroy?.();
      } catch (e) {}
      spallaPlayerRef.current = null;
    }

    if (playerRef.current) {
      const iframes = playerRef.current.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        iframe.src = 'about:blank';
        iframe.remove();
      });
      playerRef.current.innerHTML = '';
    }
  };

  const initializeSpalla = async () => {
    if (!playerRef?.current || (!isLive && !spallaConfigRef.current.currentVideoId)) return;

    destroyPlayer();

    const currentVid = spallaConfigRef.current.currentVideoId;
    const hasSubtitles = isLive ? false : await getSpallaCheckSubtitles(currentVid);

    clearTimeout(timeoutRef.current);

    const controlsDefault = [
      'play',
      'pause',
      'fullscreen',
      'volume',
      'quality',
      'info',
      'backward',
      'forward',
    ];
    const controlsLive = [...controlsDefault, 'live'];
    const controls = [...controlsDefault, 'time', 'seekbar', 'subtitles'];

    const platformControls = Platform.isTV ? '-' : isLive ? controlsLive : controls;
    const disableAds = ads?.disableAds || program.ads?.disableAds || isUnder18;

    const adTagUrl = disableAds
      ? '-'
      : Platform.isWebTV
      ? ads?.ctvVast
      : modifyVastUrl(ads?.vast, program);

    const config: any = {
      width: '100%',
      height: '100%',
      messages: true,
      bar: true,
      pip: false,
      search: false,
      muted: muted,
      autoplay: autoplay && (!Platform.isTV || isVisible),
      audioplayer: false,
      offline: false,
      subtitlePosition: 'inner',
      subtitles: hasSubtitles,
      aid: '11111111-2222-4bb6-4444-000000000000',
      info: true,
      resume: false,
      playbackSpeeds: ['-'],
      controls: platformControls,
      ima_cust_params: {
        adTagUrl,
      },
    };

    if (isLive) {
      config.t = 20;
      config.live = true;
      config.liveId = streamId;
    } else {
      config.videoId = currentVid;
    }

    const spallaPlayer = new window.SpallaPlayer(playerRef.current, config);
    spallaPlayerRef.current = spallaPlayer;

    window?.addEventListener('message', handleSpallaMessage);

    spallaPlayer.on('*', (e: any) => {
      if (onPlayerEvent) onPlayerEvent(e);
      setIsLiveRealTime(e?.isLiveRealTime);

      switch (e.event) {
        case 'ready':
          if (Platform.isTV) {
            setTimeout(() => {
              try {
                playerControls.play();
              } catch (playError) {
                console.error('SpallaPlayer TV autoplay failed', playError);
              }
            }, 300);
          } else {
            play();
          }
          setIsSpallaReady(true);
          isSpallaReadyRef.current = true;

          if (isLive || e.isAdsPlaying) break;
          handleStart(e);
          break;
        case 'start':
          if (isLive || Platform.isTV) return;

          handleStart(e);
          break;
        case 'progress':
          if (isLive || e.isAdsPlaying) break;

          if (e.id && e.id !== spallaConfigRef.current.currentVideoId) {
            break;
          }

          if (e.currentTime > spallaConfigRef.current.progress) {
            spallaConfigRef.current.progress = e.currentTime;
            setCurrentTimeState(e.currentTime);
          }
          break;
        case 'seek':
          if (isLive) break;

          if (e.id && e.id !== spallaConfigRef.current.currentVideoId) {
            break;
          }

          spallaConfigRef.current.progress = e.currentTime;
          setCurrentTimeState(e.currentTime);
          break;
        case 'pause':
          analytics.triggerMediaPauseActionEvent({
            programId: program.id,
            programTitle: program.title,
            programAgeRating: program.rating,
            programAdUnit: program.ads?.adunit?.val!,
            programCategory: program.category?.map((tag: any) => tag.name).join(', '),
            programDirector: program.director,
            programGenre: program.genre.map((genre: any) => genre.name).join(', '),
            programYear: program.year,
            programProducer: program.ads?.adunit?.name,
          });
          pause();
          break;
        case 'play':
          analytics.triggerMediaPlayActionEvent({
            programId: program.id,
            programTitle: program.title,
            programAgeRating: program.rating,
            programAdUnit: program.ads?.adunit?.val!,
            programCategory: program.category?.map((tag: any) => tag.name).join(', '),
            programDirector: program.director,
            programGenre: program.genre.map((genre: any) => genre.name).join(', '),
            programYear: program.year,
            programProducer: program.ads?.adunit?.name,
          });
          play();
          break;
        case 'fullScreen':
          handleFullScreen();
          break;
        case 'error':
          console.error('SpallaPlayer error', e);
          break;
      }
    });
  };

  useEffect(() => {
    if (!isFocused) {
      destroyPlayer();
      return;
    }

    initializeSpalla();

    return () => {
      clearTimeout(timeoutRef.current);
      window?.removeEventListener('message', handleSpallaMessage);
      destroyPlayer();
      setCurrentTimeState(0);
    };
  }, [videoId, videoIdDub, streamId, isLive, isFocused]);

  useEffect(() => {
    if (!playerRef.current) return;

    Object.assign(playerRef.current, {
      seek(time: number) {
        playerControls.seekTo(time);
      },
      progress(): PlayerMediaProgress {
        return { currentTime: currentTimeState, duration: playerDuration };
      },
    });
  }, [playerRef, currentTimeState]);

  if (isOffAir) {
    return (
      <div
        style={{ position: 'relative', overflow: 'hidden', width: '100%', paddingTop: '56.25%' }}>
        <img
          src={bandThumb}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div
        ref={playerRef}
        id="spalla-player"
        key={isLive ? streamId : videoIdDub ?? videoId}
        style={{
          ...styles.playerElement,
          pointerEvents: showNextTrack ? 'none' : 'auto',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {!showNextTrack && !Platform.isTV && (
        <div
          className="theo-top-controlbar"
          style={{
            ...styles.topControlBar,
            position: isFullScreen ? 'fixed' : 'absolute',
            opacity: isHovered ? 1 : 0,
          }}
          dangerouslySetInnerHTML={{
            __html: renderToString(
              <VideoInfoView
                title={program?.title || ''}
                image={[program?.imageVertical?.url || '', program?.imageHorizontal?.url || '']}
                programId={program?.id || ''}
                programUrl={program?.url || ''}
                ratingDescription={program?.rateDescription || ''}
                ratingValue={program?.rating || ''}
                videoTracks={[]}
              />
            ),
          }}
        />
      )}

      {!Platform.isTV && showNextTrack && (
        <div style={styles.nextCardContainer}>{autoNextCard}</div>
      )}

      {!Platform.isTV && !isSpallaReady && (
        <div style={styles.loadingContainerSpalla}>
          <ActivityIndicator size="large" color="#ffffff" />
        </div>
      )}
    </div>
  );
};

const styles = {
  wrapper: Platform.isTV
    ? { overflow: 'hidden', width: '100vw', height: '100vh', backgroundColor: '#000' }
    : { overflow: 'hidden', width: '100%', paddingTop: '56.25%', backgroundColor: '#000' },
  playerElement: Platform.isTV
    ? { width: '100%', height: '100%' }
    : { position: 'absolute', width: '100%', height: '100%', inset: 0, zIndex: 0 },
  topControlBar: {
    top: 0,
    left: 0,
    right: 0,
    width: 'inherit',
    padding: 20,
    color: 'white',
    zIndex: 1,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',
    background:
      'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  nextCardContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    marginBottom: 20,
    paddingRight: 20,
    zIndex: 2,
  },
  loadingContainerSpalla: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    inset: 0,
    backgroundColor: '#000000',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
} as const;

export default userRequired(SpallaPlayerWeb);
